import axios from 'axios';
import { call, put, select, takeLatest } from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';
import { api } from 'services/api';
import { notificationContainer } from 'utils/notificationContainer';
import { getPrpPostalOperatorSettingsLastListParams } from './selectors';
import {
	activatePrpRequest,
	getPrpPostalOperatorSettingsFailure,
	getPrpPostalOperatorSettingsRequest,
	getPrpPostalOperatorSettingsSuccess,
	getPrpSettingsFailure,
	getPrpSettingsRequest,
	getPrpSettingsSuccess,
	savePrpPostalOperatorSettingsFailure,
	savePrpPostalOperatorSettingsSuccess,
	savePrpSettingsFailure,
	savePrpSettingsSuccess,
	updatePrpPostalOperatorSettingsRequest,
	updatePrpSettingsRequest,
} from './reducer';
import {
	TActivatePrpRequest,
	TGetPrpPostalOperatorSettingsRequest,
	TPrpEmIdRequest,
	TPrpPostalOperatorSettingsListResponse,
	TPrpSettings,
	TUpdatePrpPostalOperatorSettingsRequest,
	TUpdatePrpSettingsRequest,
} from './types';
import { ESortDirection } from '../../../ui/EasTable/types';

/** Shows the message the API returned, falling back to the passed default. */
function notifyError(error: unknown, fallback: string) {
	if (axios.isAxiosError(error)) {
		notificationContainer(
			String(error?.response?.data?.message ?? error.message ?? fallback),
			'error',
		);
		return;
	}
	notificationContainer(fallback, 'error');
}

// ========================================== Settings

function* getPrpSettingsWorker({ payload }: PayloadAction<TPrpEmIdRequest>) {
	try {
		const response: TPrpSettings = yield call(api.prp.getSettings, payload);
		yield put(getPrpSettingsSuccess(response));
	} catch (error) {
		yield put(getPrpSettingsFailure());
		notifyError(error, 'Failed to load PRP settings');
	}
}

function* updatePrpSettingsWorker({ payload }: PayloadAction<TUpdatePrpSettingsRequest>) {
	try {
		const response: TPrpSettings = yield call(api.prp.updateSettings, payload);
		yield put(getPrpSettingsSuccess(response));
		yield put(savePrpSettingsSuccess());
		notificationContainer('PRP settings were successfully updated', 'success');
		yield put(
			getPrpPostalOperatorSettingsRequest({
				emId: payload.emId,
				body: {
					pageSize: 100,
					pageNumber: 1,
					orderBy: [{ fieldName: 'id', direction: ESortDirection.DESC }],
					searchString: null,
					filters: [],
				},
			}),
		);
	} catch (error) {
		yield put(savePrpSettingsFailure());
		notifyError(error, 'Failed to update PRP settings');
	}
}

function* activatePrpWorker({ payload }: PayloadAction<TActivatePrpRequest>) {
	try {
		const response: TPrpSettings = yield call(api.prp.activate, payload);
		yield put(getPrpSettingsSuccess(response));
		yield put(savePrpSettingsSuccess());
		notificationContainer('PRP was successfully activated', 'success');
		yield put(
			getPrpPostalOperatorSettingsRequest({
				emId: payload.emId,
				body: {
					pageSize: 100,
					pageNumber: 1,
					orderBy: [{ fieldName: 'id', direction: ESortDirection.DESC }],
					searchString: null,
					filters: [],
				},
			}),
		);
	} catch (error) {
		yield put(savePrpSettingsFailure());
		notifyError(error, 'Failed to activate PRP');
	}
}

// ========================================== Postal operator settings

function* getPrpPostalOperatorSettingsWorker({
	payload,
}: PayloadAction<TGetPrpPostalOperatorSettingsRequest>) {
	try {
		const response: TPrpPostalOperatorSettingsListResponse = yield call(
			api.prp.getPostalOperatorSettings,
			payload,
		);
		yield put(getPrpPostalOperatorSettingsSuccess(response));
	} catch (error) {
		yield put(getPrpPostalOperatorSettingsFailure());
		notifyError(error, 'Failed to load postal operator settings');
	}
}

function* refetchPostalOperatorSettings() {
	const lastParams: TGetPrpPostalOperatorSettingsRequest | null = yield select(
		getPrpPostalOperatorSettingsLastListParams,
	);
	if (!lastParams) return;
	const response: TPrpPostalOperatorSettingsListResponse = yield call(
		api.prp.getPostalOperatorSettings,
		lastParams,
	);
	yield put(getPrpPostalOperatorSettingsSuccess(response));
}

function* updatePrpPostalOperatorSettingsWorker({
	payload,
}: PayloadAction<TUpdatePrpPostalOperatorSettingsRequest>) {
	try {
		yield call(api.prp.updatePostalOperatorSettings, payload);
		yield call(refetchPostalOperatorSettings);
		yield put(savePrpPostalOperatorSettingsSuccess());
		notificationContainer('Postal operator settings were successfully updated', 'success');
	} catch (error) {
		yield put(savePrpPostalOperatorSettingsFailure());
		notifyError(error, 'Failed to update postal operator settings');
	}
}

export function* prpSaga() {
	yield takeLatest(getPrpSettingsRequest.type, getPrpSettingsWorker);
	yield takeLatest(updatePrpSettingsRequest.type, updatePrpSettingsWorker);
	yield takeLatest(activatePrpRequest.type, activatePrpWorker);
	yield takeLatest(getPrpPostalOperatorSettingsRequest.type, getPrpPostalOperatorSettingsWorker);
	yield takeLatest(
		updatePrpPostalOperatorSettingsRequest.type,
		updatePrpPostalOperatorSettingsWorker,
	);
}
